/*
 * Shows the components of a Shopify Bundles product inside the product info block.
 * Liquid can't read bundle components, so they are fetched from the Storefront API
 * (tokenless access) and rendered per variant. Non-bundle products stay hidden.
 */
if (!customElements.get('bundle-contents')) {
  const QUERY = `query BundleContents($handle: String!, $country: CountryCode, $language: LanguageCode)
    @inContext(country: $country, language: $language) {
    product(handle: $handle) {
      variants(first: 100) {
        nodes {
          id
          requiresComponents
          components(first: 25) {
            nodes {
              quantity
              productVariant {
                title
                image { url altText }
                product { title featuredImage { url altText } }
              }
            }
          }
        }
      }
    }
  }`;

  const requests = {};

  const fetchComponents = (handle) => {
    if (requests[handle]) return requests[handle];

    const locale = (window.Shopify?.locale || '').toUpperCase().replace('-', '_');
    const variables = { handle };
    if (window.Shopify?.country) variables.country = window.Shopify.country;
    if (locale) variables.language = locale;

    requests[handle] = fetch('/api/2025-10/graphql.json', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ query: QUERY, variables }),
    })
      .then((response) => response.json())
      .then(({ data }) => {
        const byVariant = {};
        (data?.product?.variants?.nodes || []).forEach((variant) => {
          if (!variant.requiresComponents) return;
          byVariant[variant.id.split('/').pop()] = variant.components.nodes;
        });
        return byVariant;
      })
      .catch(() => ({}));

    return requests[handle];
  };

  class BundleContents extends HTMLElement {
    connectedCallback() {
      this.list = this.querySelector('[data-bundle-list]');
      this.onVariantChange = (event) => {
        if (event.detail?.sectionId !== this.dataset.section || !event.detail.variant) return;
        this.render(String(event.detail.variant.id));
      };
      document.addEventListener('variant:change', this.onVariantChange);
      this.render(this.dataset.variantId);
    }

    disconnectedCallback() {
      document.removeEventListener('variant:change', this.onVariantChange);
    }

    async render(variantId) {
      const byVariant = await fetchComponents(this.dataset.handle);
      const components = byVariant[variantId] || [];

      this.list.replaceChildren(...components.map((component) => this.renderItem(component)));

      // In the theme editor, show a hint on non-bundle products so the block can be found
      const showNote = components.length === 0 && Boolean(window.Shopify?.designMode);
      const note = this.querySelector('[data-bundle-note]');
      if (note) note.hidden = !showNote;
      this.hidden = components.length === 0 && !showNote;
    }

    renderItem({ quantity, productVariant }) {
      const item = document.createElement('li');
      item.className = 'bundle-contents__item';

      const image = productVariant.image || productVariant.product.featuredImage;
      const media = document.createElement('div');
      media.className = 'bundle-contents__media';
      if (image) {
        const size = Number(this.dataset.imageSize) || 56;
        const img = document.createElement('img');
        const url = new URL(image.url);
        url.searchParams.set('width', String(size * 3));
        img.src = url.toString();
        img.alt = image.altText || productVariant.product.title;
        img.width = size;
        img.height = size;
        img.loading = 'lazy';
        media.appendChild(img);
      }

      const text = document.createElement('div');
      text.className = 'bundle-contents__text';

      const title = document.createElement('span');
      title.className = 'bundle-contents__title';
      title.textContent = productVariant.product.title;
      text.appendChild(title);

      if (this.dataset.showVariant === 'true' && productVariant.title !== 'Default Title') {
        const variant = document.createElement('span');
        variant.className = 'bundle-contents__variant';
        variant.textContent = productVariant.title;
        text.appendChild(variant);
      }

      item.append(media, text);

      if (this.dataset.showQuantity === 'true') {
        const qty = document.createElement('span');
        qty.className = 'bundle-contents__qty';
        qty.textContent = `${quantity}×`;
        item.appendChild(qty);
      }

      return item;
    }
  }

  customElements.define('bundle-contents', BundleContents);
}
